import { fetchApi } from "~vue/api";
import type { CreateDevelopmentGoalRequest } from "~vue/api/generated/development-journeys";
import type { DevelopmentGoalDraft } from "~vue/types/chat";
import { ref } from "vue";

type UseDevelopmentGoalSaveOptions = {
    coachingSessionUuid: string;
    setError: (message: string | null) => void;
};

const DEVELOPMENT_GOAL_SAVE_ERROR = "We couldn't save this goal. Please try again.";

export function useDevelopmentGoalSave({ coachingSessionUuid, setError }: UseDevelopmentGoalSaveOptions) {
    const savingGoal = ref<DevelopmentGoalDraft | null>(null);
    const savedGoal = ref<DevelopmentGoalDraft | null>(null);

    const isSaved = (goal: DevelopmentGoalDraft) => savedGoal.value === goal;

    const saveDevelopmentGoal = async (goal: DevelopmentGoalDraft): Promise<boolean> => {
        if (isSaved(goal)) {
            return true;
        }

        if (savingGoal.value) {
            return false;
        }

        savingGoal.value = goal;
        setError(null);
        const request: CreateDevelopmentGoalRequest = {
            ...goal,
            target_date: goal.target_date?.trim() || null,
            coaching_session_uuid: coachingSessionUuid,
        };

        try {
            const response = await fetchApi("/v1/development-journeys/goals", {
                method: "POST",
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => null);
                setError(data?.message || DEVELOPMENT_GOAL_SAVE_ERROR);
                return false;
            }

            savedGoal.value = goal;
            return true;
        } catch {
            setError(DEVELOPMENT_GOAL_SAVE_ERROR);
            return false;
        } finally {
            savingGoal.value = null;
        }
    };

    return {
        saveDevelopmentGoal,
    };
}
